/** @format */

import React, { useEffect, memo } from 'react';
import { Button, Grid } from '@mui/material';
import {
  PageContainer,
  StepperKeeper,
  ButtonLoading,
  LoadingPage,
  ConfirmDialog,
  Icons,
} from 'components';
import { useForm, FormProvider } from 'react-hook-form';
import { useStyles } from './Style';
import { useNavigate, useParams } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll';
import { BaseInfo, Constraints, Overview } from './components';
import CreateDiscountServices from './services/CreateSubscriptionServices';
import createToast from 'components/Toast/Toast';
import useCustomHook from '../hooks';
import { getDynamicObjectAction, constBreadCrumbsTitle } from 'utility';
import { useCurrencyUnit} from 'hooks';

const tabsFields = {
  baseInfo: [
    'name',
    'type',
    'price',
    'maxDebtorAmount',
    'settlementPeriodTypeCode',
    'periodTypeCount',
    'periodTypeCode',
    'usageCountLimit',
    'usageAmountLimit',
    'guildCode',
    'taxRate',
    'oneTimeUse',
  ],
  constraints: ['gildUsages', 'permittedBusinessId', 'serviceUsage'],
};

const CreateDiscount = () => {
  const INIT_TABS_ERRORS_COUNT = [0, 0];
  const currencyText = useCurrencyUnit(false);

  const {
    activeStep,
    setActiveStep,
    noLimitSelected,
    setNoLimitSelected,
    tabsErrors,
    setTabsErrors,
    fetching,
    setFetching,
    productId,
    setProductId,
    beneficiary,
    setBeneficiary,
    isConfirmOpen,
    setIsConfirmOpen,
    consumeType,
    setConsumeType,
    selectedProducts,
    setSelectedProducts,
    selectedGuilds,
    setSelectedGuilds,
    selectedBusinesses,
    loading,
    setLoading,
    setSelectedBusinesses,
    stakeholderList,
    setStakeholderList,
  } = useCustomHook();
  const navigate = useNavigate();
  const methods = useForm();
  const { handleSubmit, formState: { errors }, getValues } = methods;
  const classes = useStyles();
  const params = useParams();
  const actionsList = getDynamicObjectAction([
    'createDefineSubscriptionsPage',
    'backDefineSubscriptionsBtn',
    'editSubscriptionsBtn',
    'newSubscriptionBtn',
  ]);
  useEffect(() => {
    calculateTabsErrosCount(errors);
    if (Object.keys(errors)?.length > 0) {
      setNoLimitSelected(checkHasErrorOnLimits());
    }
  }, [errors]);

  useEffect(() => {
    if (params?.id) {
      getSubscription(params.id);
    }
  }, [params]);

  // -- API REQUESTS --//
  const MemoBaseInfo = memo(Constraints);
  async function getSubscription(id) {
    setFetching(true);
    try {
      let res = null;
      res = await CreateDiscountServices.getSubscription(id);

      setFetching(false);

      if (res.result[0].stakeholderList) {
        setStakeholderList(res.result[0].stakeholderList);
      }

      methods.reset({
        ...res.result[0],
        type: res.result[0]?.typeCode,
        periodTypeCount: res.result[0]?.periodCount ?? null,
        taxRate: res.result[0]?.tax ?? null,
        oneTimeUse: res.result[0]?.onTimeUse ?? false,
        maxDebtorAmount: res.result[0]?.postPaidMaxDebtorAmount ?? null,
        settlementPeriodTypeCode: res.result[0]?.postPaidSettlementPeriodType ?? null,
      });
      setProductId(res.result[0]?.product?.id ?? null);
      setActiveStep(0);
      setSelectedBusinesses([...res.result[0]?.permittedBusinessList]);
      setSelectedGuilds([...res.result[0]?.permittedGuildList]);
      setSelectedProducts([...res.result[0]?.permittedProductList]);
    } catch (e) {
      setFetching(false);
    }
  }

  function submitForCheck(e) {
    e?.preventDefault?.();
    setIsConfirmOpen(true);
  }

  async function postSubscription(data) {
    setTabsErrors(...INIT_TABS_ERRORS_COUNT);
    const hasErrorOnLimits = checkHasErrorOnLimits();
    setNoLimitSelected(hasErrorOnLimits);
    if (hasErrorOnLimits) {
      setTabsErrors([1, 0]);
      return;
    }
    setNoLimitSelected(true);

    const body = {
      name: data?.name,
      price: parseInt(data?.price),
      periodTypeCode: data?.periodTypeCode,
      periodTypeCount: data?.periodTypeCount ? parseInt(data?.periodTypeCount) : null,
      usageCountLimit:
        data?.type === 'SUBSCRIPTION_PLAN_TYPE_BLOCK'
          ? null
          : data?.usageCountLimit
          ? parseInt(data?.usageCountLimit)
          : null,
      usageAmountLimit:
        data?.type === 'SUBSCRIPTION_PLAN_TYPE_BLOCK'
          ? null
          : data?.usageAmountLimit
          ? parseInt(data?.usageAmountLimit)
          : null,
      type: data?.type,
      guildCode: data?.guildCode?.code,
      permittedGuildCode: [...selectedGuilds.map((v) => v?.code)],
      permittedBusinessId: [...selectedBusinesses.map((v) => v?.id)],
      permittedProductId: [...selectedProducts.map((v) => v?.id)], // id stands for entityId
      currencyCode: 'IRR',
      oneTimeUse: data?.oneTimeUse,
      taxRate: data?.taxRate ? parseInt(data?.taxRate) : null,
      ...beneficiary,
    };
    if (data?.type === 'SUBSCRIPTION_PLAN_TYPE_POST_PAID') {
      body.maxDebtorAmount = data.maxDebtorAmount ? parseInt(data?.maxDebtorAmount) : null;
      body.settlementPeriodTypeCode = data?.settlementPeriodTypeCode;
    }

    setLoading(true);
    try {
      let res = null;
      if (params?.id) {
        const reformedBody = { ...body };
        delete reformedBody.guildCode;
        res = await CreateDiscountServices.putSubscription({
          ...reformedBody,
          id: params.id,
          productId,
        });
      } else {
        res = await CreateDiscountServices.postSubscription(body);
      }
      createToast(
        'موفق',
        params?.id ? 'طرح اشتراک با موفقیت ویرایش شد.' : 'طرح اشتراک با موفقیت ایجاد شد!',
        'success',
      );

      setLoading(false);
      scroll.scrollToTop();
      if (!params?.id) {
        navigate('/subscriptions/assigned');
      }
      setActiveStep(0);
    } catch (e) {
      setLoading(false);
    }
  }

  // -- OTHER FUNCTIONS--//
  const checkHasErrorOnLimits = () => {
    return false;
  };

  const flattenErros = (errs) => {
    if (!errs) return null;
    if (Object.keys(errs).length === 0) return null;
    const tempErrs = { ...errs };
    return tempErrs;
  };

  const calculateTabsErrosCount = (errs) => {
    setTabsErrors([...INIT_TABS_ERRORS_COUNT]);
    if (!errs) return;
    const tempErrs = flattenErros(errs);
    if (!tempErrs) return;

    const newTabsErros = [...INIT_TABS_ERRORS_COUNT];

    Object.keys(tempErrs).map((v) => {
      Object.keys(tabsFields).map((tabFields, k) => {
        if (
          tabsFields[tabFields].includes(tempErrs[v]?.ref?.name) ||
          tabsFields[tabFields].includes(tempErrs[v]?.name)
        ) {
          newTabsErros[k] = newTabsErros[k] + 1;
        }
      });
    });

    setTabsErrors(checkHasErrorOnLimits() ? [newTabsErros[0] + 1, newTabsErros[1]] : newTabsErros);
  };

  const hint = params?.id
    ? 'شما در این بخش می‌توانید طرح اشتراک مورد نظر را ویرایش کنید.'
    : 'شما در این بخش می‌توانید طرح اشتراک جدید تعریف نمایید.';

  const getKeyBreadCrumbsWithUrl = () => {
    if (!params) return null;

    return params.id
      ? constBreadCrumbsTitle.SUBSCRIPTIONS_EDIT
      : constBreadCrumbsTitle.SUBSCRIPTIONS_CREATE;
  };
  return (
    <PageContainer
      subTitle={hint}
      title={params?.id ? 'ویرایش طرح اشتراک' : 'ایجاد طرح اشتراک'}
      meta={
        <>
          {actionsList['backDefineSubscriptionsBtn'] && (
            <Button
              color="primary"
              variant="outlined"
              endIcon={<Icons icon="CaretLeft" color="#2b3990" size="25" />}
              onClick={() => navigate('/subscriptions/assigned')}
            >
              بازگشت به لیست طرح‌ها
            </Button>
          )}
        </>
      }
      breadcrumbs={getKeyBreadCrumbsWithUrl()}
    >
      {(actionsList['createDefineSubscriptionsPage'] ||
        actionsList['editSubscriptionsBtn'] ||
        actionsList['newSubscriptionBtn']) && (
        <>
          {fetching ? (
            <LoadingPage />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={submitForCheck} noValidate autoComplete="off">
                <StepperKeeper
                  titles={[
                    { label: 'اطلاعات پایه', liner: 'dotted' },
                    { label: 'تنظیمات پیشرفته (محدودیت‌ها)' },
                  ]}
                  tabsErrors={tabsErrors}
                  forceSelectedTabIndex={activeStep}
                  onStepChanged={setActiveStep}
                  stepsToHideNext={'false'}
                  panels={[
                    <BaseInfo id={params?.id} noLimitSelected={noLimitSelected} />,
                    <Constraints
                      consumeType={consumeType}
                      setConsumeType={setConsumeType}
                      selectedProducts={selectedProducts}
                      setSelectedProducts={setSelectedProducts}
                      selectedGuilds={selectedGuilds}
                      setSelectedGuilds={setSelectedGuilds}
                      selectedBusinesses={selectedBusinesses}
                      setSelectedBusinesses={setSelectedBusinesses}
                      setBeneficiary={setBeneficiary}
                      beneficiary={beneficiary}
                      stakeholderList={stakeholderList}
                    />,
                  ]}
                  formAction={
                    <Grid container item xs={12} justifyContent="flex-end">
                      <div className={classes.containButton}>
                        <ButtonLoading
                          variant="contained"
                          color="primary"
                          loading={loading}
                          name={`تایید و ${params?.id ? 'ویرایش' : 'ایجاد'} طرح اشتراک`}
                          type="submit"
                        />
                      </div>
                    </Grid>
                  }
                />

                {/* Confirm Voucher Modal */}
                <ConfirmDialog
                  open={isConfirmOpen}
                  onClose={() => setIsConfirmOpen(false)}
                  onConfirm={() => {
                    setIsConfirmOpen(false);
                    handleSubmit(postSubscription)();
                  }}
                  title="تایید ایجاد طرح اشتراک"
                  confirmButtonText="ایجاد طرح اشتراک"
                  cancelButtonProps="انصراف"
                >
                  <Overview
                    guilds={selectedGuilds}
                    businesses={selectedBusinesses}
                    products={selectedProducts}
                    consumeType={consumeType}
                    selectedGuilds={selectedGuilds}
                    selectedBusinesses={selectedBusinesses}
                    currencyText={currencyText}
                  />
                </ConfirmDialog>
              </form>
            </FormProvider>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default CreateDiscount;
